// Route `api` cho đơn nghỉ phép Mee (B-06): JSON của `/v1/me/leave` và `/v1/team/leave` (TblLeaveRequest, 22 cột, đã lọc phạm vi ở worker)
// → `MeeRequest` của màn. Chỉ có đơn phép; tăng ca / công tác / giải trình công chưa có bảng nên vẫn là dữ liệu mẫu.
import type { ApiRoute, ApiRoutes } from '@hana/data';
import type { MeeRequest, RequestStatus } from './types';

type Row = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const day = (v: unknown): string | undefined => /^\d{4}-\d{2}-\d{2}/.exec(str(v))?.[0];
const hhmm = (v: unknown): string | undefined => /^\d{1,2}:\d{2}/.exec(str(v))?.[0]?.padStart(5, '0');
const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd');

/** Status của TblLeaveRequest → trạng thái đơn của màn. Giá trị lạ coi là chờ duyệt (không cho thấy đã duyệt khi chưa chắc). */
export function statusFromApi(v: unknown): RequestStatus {
  const s = fold(str(v));
  if (/reject|tu choi|deny|denied/.test(s)) return 'Rejected';
  if (/cancel|huy|rut/.test(s)) return 'Cancelled';
  if (/approv|duyet|da chap/.test(s) && !/cho|pending|wait/.test(s)) return 'Approved';
  return 'Pending';
}

export function leaveFromApi(r: Row): MeeRequest {
  const half = r.IsHalfDay === true ? (/^(pm|chieu|afternoon)/.test(fold(str(r.HalfDayPeriod))) ? 'PM' : 'AM') : undefined;
  const from = day(r.StartDate) ?? '';
  const to = day(r.EndDate);
  const qty = typeof r.TotalDays === 'number' ? r.TotalDays : Number.parseFloat(str(r.TotalDays));
  return {
    id: str(r.LeaveCode),
    owner: str(r.EmployeeCode),
    type: 'leave',
    sub: str(r.LeaveType) || 'Phép năm',
    from,
    ...(to ? { to } : {}),
    ...(Number.isFinite(qty) ? { qty } : {}),
    ...(half ? { half } : {}),
    ...(half && hhmm(r.StartTime) ? { t0: hhmm(r.StartTime) } : {}),
    ...(half && hhmm(r.EndTime) ? { t1: hhmm(r.EndTime) } : {}),
    status: statusFromApi(r.Status),
    // bảng không có ngày gửi; ngày duyệt (nếu có) là mốc gần nhất
    sent: day(r.ApprovedDate) ?? '',
  };
}

const route = (path: string): ApiRoute<MeeRequest> => ({
  path,
  // worker phân trang theo `page`; màn Mee lấy năm đang xem qua `where.year`, trạng thái qua `where.status`
  query: (q) => ({ page: q.limit ? Math.floor((q.offset ?? 0) / q.limit) + 1 : undefined, limit: q.limit, status: q.where?.status as string | undefined, year: q.where?.year as number | undefined }),
  parse: (json) => {
    const j = (json ?? {}) as { items?: Row[]; hasMore?: boolean };
    return { items: (j.items ?? []).map(leaveFromApi).filter((x) => x.id), hasMore: j.hasMore };
  },
});

/** `meeLeaveMine` = đơn của mình · `meeLeaveTeam` = đơn của người báo cáo trực tiếp (worker lọc theo phiên đăng nhập). */
export const meeApiRoutes: ApiRoutes = { meeLeaveMine: route('/api/v1/me/leave'), meeLeaveTeam: route('/api/v1/team/leave') };

/**
 * Gộp đơn: đơn phép lấy từ `mine` + `team` (nguồn có phạm vi đúng), đơn loại khác giữ từ `base`.
 * Nguồn `base` còn đơn phép (dữ liệu mẫu) thì bỏ đi để không lẫn với dữ liệu thật; nếu cả `mine` và `team` rỗng giữ nguyên `base`.
 */
export function mergeLeave(base: readonly MeeRequest[], mine: readonly MeeRequest[], team: readonly MeeRequest[]) {
  const mineIds = new Set(mine.map((r) => r.id));
  const teamIds = new Set(team.map((r) => r.id));
  if (!mine.length && !team.length) return { items: [...base], mineIds, teamIds };
  const seen = new Set<string>();
  const leave = [...mine, ...team].filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
  return { items: [...leave, ...base.filter((r) => r.type !== 'leave')], mineIds, teamIds };
}
